import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Button from "../Button";
import Modal from "../Modal";
import Input from "../Input";
import InputSelect from "../InputSelect";
import InputTextarea from "../InputTextarea";
import { Loading } from "../Loading";
import api from "../../services/api";
import { renderError } from "../../helpers/errors";

/**
 * Edição de um registro sem sair da tela: recebe o endpoint e a descrição dos
 * campos, carrega no GET e devolve no PUT.
 *
 * Existe porque mandar o usuário para a tela de cadastro por causa de um campo
 * custa a ida, a volta e o `return_to` — e cada tela que precisava disso
 * reescrevia o mesmo formulário.
 *
 * `fields` aceita campos soltos ou grupos (`{ title, fields: [] }`), e cada
 * campo é `{ name, label, type, col, mask, options, hint, autofill }`.
 *
 * `keep` é a parte não óbvia: a API valida o recurso INTEIRO, então campos que
 * o modal não mostra (um `type` obrigatório, por exemplo) precisam voltar como
 * vieram — sem isso o PUT é recusado por um campo que o usuário nem viu.
 */
export const ModalEditResource = ({
    show,
    close,
    title,
    endpoint,
    fields = [],
    keep = [],
    resourceKey = 'resource',
    saveText = 'Salvar',
    size,
    level,
    intro,
    highlight = [],
    onSaved,
}) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({});
    const [resource, setResource] = useState(null);

    useEffect(() => {
        if (show && endpoint) load();
    }, [show, endpoint]);

    const groups = fields.map(item => (
        Array.isArray(item?.fields) ? item : { title: null, fields: [item] }
    ));

    const allFields = groups.flatMap(group => group.fields);

    const load = () => {
        setLoading(true);

        api.get(endpoint).then(res => {
            const loaded = res.data[resourceKey] ?? {};

            setResource(loaded);
            setForm(Object.fromEntries(
                allFields.map(field => [field.name, loaded[field.name] ?? ''])
            ));
        }).catch(renderError).then(() => setLoading(false));
    };

    const updateForm = changes => setForm(current => ({ ...current, ...changes }));

    /**
     * `autofill` roda só na digitação, nunca no load: no carregamento ele
     * atropelaria o endereço que o usuário ajustou à mão.
     */
    const changeField = async (field, value) => {
        updateForm({ [field.name]: value });

        if (typeof field.autofill !== 'function') return;

        const filled = await field.autofill(value);

        if (filled) updateForm(filled);
    };

    const save = () => {
        setSaving(true);

        const payload = { ...form };

        keep.forEach(name => {
            payload[name] = resource?.[name] ?? null;
        });

        api.put(endpoint, payload).then(res => {
            toast.success('Dados salvos');

            if (typeof onSaved === 'function') onSaved(res.data[resourceKey]);

            close();
        }).catch(renderError).then(() => setSaving(false));
    };

    const renderField = field => {
        const value = form[field.name] ?? '';

        if (field.type === 'select') {
            return (
                <InputSelect
                    menuInBody
                    label={field.label}
                    options={field.options ?? []}
                    isMulti={field.isMulti}
                    value={value}
                    change={selected => changeField(field, selected)}
                />
            );
        }

        if (field.type === 'textarea') {
            return (
                <InputTextarea
                    label={field.label}
                    value={value}
                    change={newValue => changeField(field, newValue)}
                />
            );
        }

        return (
            <Input
                type={field.type ?? 'text'}
                label={field.label}
                value={value}
                mask={field.mask}
                change={newValue => changeField(field, newValue)}
            />
        );
    };

    return (
        <Modal
            title={title}
            show={show}
            close={close}
            size={size}
            level={level}
            className={`modal-edit-resource`}
            footer={
                <Button
                    type={`primary`}
                    size={`small`}
                    svg={`save-white`}
                    text={saveText}
                    action={save}
                    loading={saving}
                    disabled={loading}
                />
            }
        >
            <div className="section">
                {loading && <Loading show={true} />}

                {!loading &&
                    <>
                        {intro && <p className={`modal-edit-intro`}>{intro}</p>}

                        {groups.map((group, groupIndex) => (
                            <div key={groupIndex}>
                                {group.title &&
                                    <h2 className="section-title">{group.title}</h2>
                                }
                                <div className="row">
                                    {group.fields.map(field => (
                                        <div
                                            key={field.name}
                                            className={`col-${field.col ?? 12} ${highlight.includes(field.name) ? 'field-pending' : ''}`}
                                        >
                                            {renderField(field)}
                                            {field.hint &&
                                                <p className={`modal-edit-hint`}>{field.hint}</p>
                                            }
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </>
                }
            </div>
        </Modal>
    );
};

export default ModalEditResource;
